// EP-133 §3c (T133-021) — admin and staff dashboard visitors line. Since
// EP-155 one line: visitors expected today and those still to arrive (the
// API still returns staff_on_shift; the dashboard no longer shows it here).
//
// ROUTE: this is a dashboard-only widget. The standalone guard/front-desk
// surface is /visitor-arrivals (visitor-arrivals page); the strip is a
// glance, not a navigation entry.
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useTodayStrip } from '@/hooks/useVisitorStoday'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useFeatureFlags } from '@/lib/featureFlags'

export function TodayStrip() {
  const { t } = useTranslation('visitors')
  // EP-133 T133-025 — the same dual gate the server applies in
  // VisitorManagementModuleGate (Unleash `visitor_management` AND the
  // per-account `visitor_management_enabled` setting). It lives here rather
  // than at the call site so every caller inherits it: without it the strip
  // requested /api/dashboards/today_strip on every admin/staff dashboard load,
  // took a 403, and rendered its zero-fallback — wrong quietly rather than
  // loudly, on every account.
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()
  const enabled =
    featureFlags.visitorManagement && (accountSettings?.settings?.visitor_management_enabled ?? false)

  const { data, isLoading, isFetching } = useTodayStrip(enabled)

  if (!enabled) return null

  // EP-155: one line, visitors only. The on-shift count left it (decision
  // D9): it is the team line's number, and staff have no team line.
  if (isLoading) {
    return (
      <div className="rounded-lg border border-base-200 bg-base-100 px-4 py-3" data-testid="today-strip">
        <div className="h-4 w-48 bg-base-200 rounded animate-pulse" />
      </div>
    )
  }

  const counts = data ?? { staff_on_shift: 0, visitors_today: 0, not_yet_arrived: 0 }

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-base-200 bg-base-100 px-4 py-3 text-sm"
      data-testid="today-strip"
      aria-busy={isFetching && !isLoading}
    >
      <span data-testid="today-strip-visitors">{t('dashboard_strip.visitors_today', { count: counts.visitors_today })}</span>
      <span className="text-base-content/40" aria-hidden>·</span>
      <span className={counts.not_yet_arrived > 0 ? 'text-warning' : 'text-base-content/60'} data-testid="today-strip-not-arrived">
        {t('dashboard_strip.not_yet_arrived', { count: counts.not_yet_arrived })}
      </span>
      <Link
        to="/visitor-arrivals"
        className="ml-auto text-xs text-primary hover:underline whitespace-nowrap"
        data-testid="today-strip-view-all"
      >
        {t('dashboard_strip.view_all')}
      </Link>
    </div>
  )
}
